// Хранилище заданий расшифровки — на уровне модуля, а не компонента.
//
// Раньше job_id ловился подпиской `useUploads().subscribe(TOOL_CTX, …)` внутри
// `ToolsPage`: подписчики живут в `UploadManager`, пока смонтирован компонент,
// который подписался. Загрузка большого файла идёт минуты, рекрутёр уходит со
// страницы — `ToolsPage` размонтируется, подписка снимается, и ответ сервера
// (который приходит именно тогда, пока никто не слушает) улетает в пустоту:
// ни в localStorage, ни в список заданий он так и не попадает, хотя сервер
// задание уже создал и сам его выполнил.
//
// Поэтому захват отделён от компонента: `UploadManager` зовёт
// `captureToolUploadResponse` прямо из `runOne().then()`, независимо от того,
// открыта ли сейчас вкладка «Инструменты». `ToolsPage` только читает и
// дополняет этот же модуль через `useSyncExternalStore` — список не зависит
// от момента монтирования.
//
// Вторая причина того же «файл загрузился, и ничего»: ответ POST
// /api/tools/transcribe приходил выжимкой — без `status`, `phase` и
// `progress`, — а опрос брал только задания со `status` queued или running.
// Задание без статуса не опрашивалось никогда: строка висела в
// «Обрабатывается» с пустыми «· %», и в логах сервера не было ни одного GET.
// Сервер теперь отдаёт на POST тот же объект, что и на GET, но форме ответа
// здесь больше не доверяем: всё, что попадает в хранилище, проходит через
// `normalizeJob` — и ответ сервера, и то, что уже лежит в localStorage у
// рекрутёров (их повисшие задания после обновления страницы оживают сами).
//
// Логика разделена на чистые функции (тестируются без браузерных глобалов) и
// обёртку состояния модуля (единственное место, трогающее `localStorage`).

const LS_KEY = "newats.tools.jobs.v1";

export type JobStatus = "queued" | "running" | "done" | "failed";
export type Mode = "stt" | "stt+diarize";

export interface ServerJob {
  job_id: string;
  status: JobStatus;
  phase: string;
  progress: number;
  filename: string;
  out_filename: string;
  size_bytes: number | null;
  audio_duration_s: number | null;
  error: string | null;
  created_at: number;
  done_at: number | null;
  elapsed_s: number | null;
  mode: Mode;
  speakers?: number | null;
  eta_s?: number | null;
}

export interface JobState extends ServerJob {
  downloaded: boolean;
}

export type PollUpdate = { id: string; server: ServerJob } | { id: string; gone: true };

// ---- чистые функции: вход/выход — массивы, без глобалов и без состояния ----

const STATUSES: readonly JobStatus[] = ["queued", "running", "done", "failed"];

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** Задание с заполненными полями — из чего бы оно ни пришло: из ответа
 *  сервера, из localStorage прошлой версии страницы или из записи, которую
 *  испортили руками. Поля, по которым страница решает, что делать (`status`),
 *  и которые она рисует (`phase`, `progress`), не должны быть пустыми: без
 *  `status` задание не попадало в опрос и висело вечно. */
export function normalizeJob(raw: Record<string, unknown>): JobState {
  const status = STATUSES.includes(raw.status as JobStatus) ? (raw.status as JobStatus) : "queued";
  const progress = num(raw.progress);
  return {
    job_id: String(raw.job_id),
    status,
    // «upload» — ещё не началось: ровно то, чем задание и является сразу после
    // ответа на загрузку.
    phase: typeof raw.phase === "string" && raw.phase ? raw.phase : "upload",
    progress: progress === null ? 0 : Math.min(100, Math.max(0, Math.round(progress))),
    filename: typeof raw.filename === "string" ? raw.filename : "файл",
    out_filename: typeof raw.out_filename === "string" ? raw.out_filename : "расшифровка.txt",
    size_bytes: num(raw.size_bytes),
    audio_duration_s: num(raw.audio_duration_s),
    error: typeof raw.error === "string" ? raw.error : null,
    created_at: num(raw.created_at) ?? Date.now() / 1000,
    done_at: num(raw.done_at),
    elapsed_s: num(raw.elapsed_s),
    mode: raw.mode === "stt" ? "stt" : "stt+diarize",
    speakers: num(raw.speakers),
    eta_s: num(raw.eta_s),
    downloaded: raw.downloaded === true,
  };
}

/** Разбор того, что лежит в localStorage. Любая форма, кроме «массив объектов
 *  с строковым job_id», — как будто там ничего не было: старое поле не стоит
 *  ронять весь список из-за одной повреждённой записи. */
export function parsePersistedJobs(raw: string | null): JobState[] {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr
      .filter((j): j is Record<string, unknown> => !!j && typeof j.job_id === "string")
      .map(normalizeJob);
  } catch {
    return [];
  }
}

/** Добавить задание, если такого job_id ещё нет. Тот же массив — если
 *  добавлять нечего, незачем будить подписчиков. */
export function withAddedJob(jobs: JobState[], server: ServerJob): JobState[] {
  if (jobs.some((j) => j.job_id === server.job_id)) return jobs;
  return [normalizeJob({ ...server, downloaded: false }), ...jobs];
}

/** Результат опроса `/api/tools/jobs/{id}`: обновить дошедшие, убрать те, что
 *  сервер больше не знает (сметены TTL). */
export function withPollApplied(jobs: JobState[], updates: PollUpdate[]): JobState[] {
  if (updates.length === 0) return jobs;
  const goneIds = new Set(updates.filter((u): u is { id: string; gone: true } => "gone" in u).map((u) => u.id));
  return jobs
    .filter((j) => !goneIds.has(j.job_id))
    .map((j) => {
      const u = updates.find((x): x is { id: string; server: ServerJob } => "server" in x && x.id === j.job_id);
      return u ? normalizeJob({ ...j, ...u.server }) : j;
    });
}

export function withDownloaded(jobs: JobState[], jobId: string): JobState[] {
  return jobs.map((j) => (j.job_id === jobId ? { ...j, downloaded: true } : j));
}

export function withError(jobs: JobState[], jobId: string, error: string): JobState[] {
  return jobs.map((j) => (j.job_id === jobId ? { ...j, error } : j));
}

export function withRemoved(jobs: JobState[], jobId: string): JobState[] {
  return jobs.filter((j) => j.job_id !== jobId);
}

// ---- состояние модуля: один инстанс на вкладку, общий для всех импортёров ----

function readStorage(): JobState[] {
  try {
    return parsePersistedJobs(localStorage.getItem(LS_KEY));
  } catch {
    return [];
  }
}

function writeStorage(next: JobState[]): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(next));
  } catch {
    /* приватный режим / квота */
  }
}

let jobs: JobState[] = readStorage();
const listeners = new Set<() => void>();

function commit(next: JobState[]): void {
  if (next === jobs) return; // withX вернула тот же массив — реально ничего не изменилось
  jobs = next;
  writeStorage(jobs);
  for (const fn of listeners) fn();
}

export function getToolJobsSnapshot(): JobState[] {
  return jobs;
}

export function subscribeToolJobs(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function addToolJob(server: ServerJob): void {
  commit(withAddedJob(jobs, server));
}

export function applyToolJobsPoll(updates: PollUpdate[]): void {
  commit(withPollApplied(jobs, updates));
}

export function markToolJobDownloaded(jobId: string): void {
  commit(withDownloaded(jobs, jobId));
}

export function setToolJobError(jobId: string, error: string): void {
  commit(withError(jobs, jobId, error));
}

export function removeToolJob(jobId: string): void {
  commit(withRemoved(jobs, jobId));
}

/** Единственная точка входа из `UploadManager`: ответ XHR на
 *  `/api/tools/transcribe`, каким он придёт, — успешно завершённая загрузка
 *  tool-transcribe. Не `ServerJob`, а `unknown`: `UploadItem`-обработчик
 *  типизирован дженериком, и проверка формы — часть самого захвата, а не
 *  предположение вызывающей стороны. Чего в ответе не оказалось, то дополнит
 *  `normalizeJob`: нужен только `job_id`, остальное задание доберёт первым же
 *  опросом. */
export function captureToolUploadResponse(response: unknown): void {
  if (!response || typeof response !== "object") return;
  const srv = response as Record<string, unknown>;
  if (typeof srv.job_id !== "string") return;
  addToolJob(normalizeJob(srv));
}
